import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { DynamicFormField } from "@components/ui/DynamicInput";
import { BANK_DETAILS_FIELDS } from "../../loanApplications/formFieldsConfig/bankDetails";
import { Button } from "@components/shadcn-ui/button";
import { bankService, BankDetails, BankResponse } from "../service/bankService";
import { useToast } from "@customHooks/useToast";
import { Loader2, X } from "lucide-react";

interface BankFormProps {
  applicationId: string;
  leadId: string;
  onBankAdded: (bank: BankResponse) => void;
  onCancel: () => void;
  initialData?: BankResponse;
  isEditMode?: boolean;
}

export const BankForm: React.FC<BankFormProps> = ({
  applicationId,
  leadId,
  onBankAdded,
  onCancel,
  initialData,
  isEditMode = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const form = useForm({
    mode: "all",
    defaultValues: initialData || {},
  });

  // When initialData changes (e.g., when editing a different bank), update the form
  useEffect(() => {
    if (initialData) {
      console.log("Setting bank form values with:", initialData);
      form.reset(initialData);
    }
  }, [initialData, form]);

  // Debug log to verify props
  console.log("BankForm props:", { applicationId, leadId, isEditMode, initialData });

  const handleSubmit = async (data: any) => {
    // Additional validation to ensure IDs are present and valid
    if (!applicationId || applicationId === "undefined" || applicationId === "null") {
      toast({
        variant: "destructive",
        description: "Application ID is missing or invalid",
      });
      console.error("Invalid Application ID:", applicationId);
      return;
    }

    if (!leadId || leadId === "undefined" || leadId === "null") {
      toast({
        variant: "destructive",
        description: "Lead ID is missing or invalid",
      });
      console.error("Invalid Lead ID:", leadId);
      return;
    }

    setIsSubmitting(true);
    try {
      const bankData: BankDetails = {
        ...data,
        id: initialData?.id,
        applicationId,
        leadId,
      };

      console.log(`${isEditMode ? "Updating" : "Creating"} bank data:`, bankData);

      let response;
      if (isEditMode && initialData?.id) {
        // Update existing bank
        response = await bankService.updateBank(bankData);
        toast({
          description: "Bank account updated successfully",
        });
      } else {
        // Create new bank
        response = await bankService.createBank(bankData);
        toast({
          description: "Bank account added successfully",
        });
      }
      
      onBankAdded(response);
      form.reset();
    } catch (error) {
      console.error(`Error ${isEditMode ? "updating" : "adding"} bank account:`, error);
      toast({
        variant: "destructive",
        description: `Failed to ${isEditMode ? "update" : "add"} bank account. Please try again.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="border p-4 rounded-md mb-4 bg-gray-50">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">{isEditMode ? "Edit" : "Add New"} Bank Account</h3>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onCancel}
          className="h-8 w-8 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <FormProvider {...form}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pb-4">
          {BANK_DETAILS_FIELDS.fields.map((field, index) => (
            <div key={index} className="w-full">
              <DynamicFormField field={field} isDisabled={isSubmitting} />
            </div>
          ))}
        </div>
        
        <div className="flex justify-end mt-4 gap-2 sticky bottom-0 pt-2 bg-gray-50">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="button"
            onClick={() => form.handleSubmit(handleSubmit)()}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditMode ? "Updating..." : "Saving..."}
              </>
            ) : (
              isEditMode ? "Update Bank Account" : "Save Bank Account"
            )}
          </Button>
        </div>
      </FormProvider>
    </div>
  );
};
